/**
 * overlayDeletion.ts
 *
 * Shared delete-an-overlay sequence used by every surface that removes a
 * composition / overlay element (Creator Studio canvas action menu + left
 * transcript hover pill, Content Studio clip-preview transcript hover pill).
 * Centralising it keeps the four surfaces from drifting and puts the
 * "why" in one place (PLAT-10191).
 *
 * Why a delete needs more than `commandRemoveElement`:
 *
 *  - **Command path, not raw `timeline.removeElement`.** `commandRemoveElement`
 *    runs `RemoveElementCommand`, which marks the editor dirty, recalculates
 *    the timeline, notifies observers (renderer repaints) and registers an
 *    undo entry. The raw timeline method mutates the model in place with none
 *    of that, so the canvas never updates and the delete "does nothing".
 *  - **Immediate repaint.** The command's `markDirty` → renderer sync is
 *    debounced (~300ms); `refreshSource()` drops the element on this tick.
 *  - **Orphaned-metadata cleanup.** A marker needs BOTH a timeline element and
 *    its `extraElementData`; leaving the meta behind leaks an entry keyed to a
 *    deleted element (and can re-hydrate the marker on reload).
 *  - **Explicit persistence flush.** The save pipeline snapshots the editor
 *    *before* each command runs, so the command's own autosave captures the
 *    PRE-delete timeline. An add survives reload only because its follow-up
 *    selection / update commands snapshot the post-add state; a delete clears
 *    selection and has no such follow-up, so the post-delete state is never
 *    flushed and the element returns on reload. Each surface supplies that
 *    missing flush via `persist`.
 */

import { deleteElementMeta } from '@src/utility/extraElementData';
import { extractErrorInfo } from '@src/utility/error';

import type { VideoLike } from '@src/utility/extraElementData';

export interface OverlayDeletionContext {
  /** Editor command API — `commandRemoveElement` routes through the command path. */
  editor: { commandRemoveElement: (id: string) => boolean };
  /** Current video for orphaned-metadata cleanup; null-safe. */
  video: VideoLike | null | undefined;
  /** Immediate canvas repaint (the command's markDirty sync is debounced). */
  refreshSource: () => void;
  /** Flush the POST-delete editor state to storage (background autosave). */
  persist: () => void | Promise<unknown>;
  /** Logger for a persist failure (the flush is fire-and-forget). */
  logger: { error: (message: string, context?: Record<string, unknown>) => void };
}

/**
 * Remove an overlay / composition placement so the delete repaints AND
 * persists like an add. Returns whether the element was actually removed
 * (callers gate selection-clear / logging on this).
 */
export function removeOverlayAndPersist(
  overlayId: string,
  ctx: OverlayDeletionContext,
): boolean {
  const removed = ctx.editor.commandRemoveElement(overlayId);
  if (!removed) return false;

  ctx.refreshSource();
  if (ctx.video) deleteElementMeta(ctx.video, overlayId);

  // Fire-and-forget flush with error logging — an unhandled rejection here
  // would silently lose the persistence guarantee the fix depends on.
  void Promise.resolve(ctx.persist()).catch(err =>
    ctx.logger.error('Overlay delete persist failed', {
      overlayId,
      ...extractErrorInfo(err),
    }),
  );

  return removed;
}
